import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { theme } from '../styles/theme';
import ByteBotLogo from './ByteBotLogo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle, error, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended plan from URL params or state
  const searchParams = new URLSearchParams(location.search);
  const selectedPlan = searchParams.get('plan') || location.state?.selectedPlan;

  useEffect(() => {
    // If user is already logged in, redirect appropriately
    if (user) {
      if (selectedPlan) {
        // Redirect to subscription page with selected plan
        navigate(`/dashboard/subscription?plan=${selectedPlan}`);
      } else {
        // Just go to dashboard
        navigate('/dashboard');
      }
    }
  }, [user, selectedPlan, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      // Navigation will be handled by useEffect when user state updates
    } catch (error) {
      console.error('Login error:', error);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      // Navigation will be handled by useEffect when user state updates
    } catch (error) {
      console.error('Google sign-in error:', error);
    }
    setLoading(false);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      padding: '20px',
    },
    card: {
      backgroundColor: theme.colors.surface,
      padding: '40px',
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.borderDark}`,
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center',
    },
    header: {
      marginBottom: '30px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    title: {
      fontSize: theme.typography.fontSizes['3xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    subtitle: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.lg,
    },
    planNotice: {
      backgroundColor: theme.colors.primary + '10',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.md,
      marginBottom: theme.spacing.lg,
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.primary,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    input: {
      padding: '15px',
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
      backgroundColor: theme.colors.background,
      transition: 'all 0.2s ease',
      color: theme.colors.text,
    },
    inputFocus: {
      outline: 'none',
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.surface,
    },
    button: {
      padding: '15px',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      backgroundColor: theme.colors.primary,
      color: '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    buttonSecondary: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      border: `2px solid ${theme.colors.borderDark}`,
    },
    error: {
      color: theme.colors.primary,
      textAlign: 'center',
      padding: theme.spacing.md,
      backgroundColor: theme.colors.primary + '10',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    switchText: {
      textAlign: 'center',
      marginTop: theme.spacing.lg,
      color: theme.colors.textLight,
      fontSize: theme.typography.fontSizes.sm,
    },
    switchLink: {
      color: theme.colors.primary,
      textDecoration: 'none',
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    backLink: {
      display: 'inline-block',
      marginBottom: theme.spacing.lg,
      color: theme.colors.textLight,
      textDecoration: 'none',
      fontSize: theme.typography.fontSizes.sm,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <Link to="/" style={styles.backLink}>← [BACK TO HOME]</Link>
        
        <div style={styles.header}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <ByteBotLogo 
                size={32} 
                color={theme.colors.primary}
              />
            </div>
            [BYTE<span style={styles.logoAccent}>BOT</span>]
          </div>
          <h1 style={styles.title}>[LOGIN]</h1>
          <p style={styles.subtitle}>
            Welcome back! Sign in to your account.
          </p>
          
          {selectedPlan && (
            <div style={styles.planNotice}>
              [PLAN SELECTED: {selectedPlan.toUpperCase()}]<br/>
              You'll be redirected to checkout after login.
            </div>
          )}
        </div>

        {error && (
          <div style={styles.error}>
            [ERROR] {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
            onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
            onBlur={(e) => Object.assign(e.target.style, styles.input)}
          />
          
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
            onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
            onBlur={(e) => Object.assign(e.target.style, styles.input)}
          />

          <button
            type="submit"
            disabled={loading}
            style={styles.button}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? '[SIGNING IN...]' : '[SIGN IN]'}
          </button>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{...styles.button, ...styles.buttonSecondary}}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.background;
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.surface;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? '[LOADING...]' : '[SIGN IN WITH GOOGLE]'}
          </button>
        </form>

        <div style={styles.switchText}>
          <Link 
            to="/forgot-password" 
            style={styles.switchLink}
          >
            [FORGOT PASSWORD?]
          </Link>
        </div>

        <div style={styles.switchText}>
          Don't have an account?{' '}
          <Link 
            to={`/signup${selectedPlan ? `?plan=${selectedPlan}` : ''}`} 
            style={styles.switchLink}
          >
            [SIGN UP]
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login; 